import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { orderByName, orderByAddress, orderByExternalId } from './headersFunctions';
import { combiningObjects } from './listFormater';
import StationGroupCardInnerContent from './StationGroupCardInnerContent/StationGroupCardInnerContent';

import { getChargePointGroups } from '../../../hooks/chargepointGroups';
import { ChargePointGroupProps } from '../../../types/responses/ChargePointGroups';
import { setIconActive, cpGroupsListFilter } from '../../../utils/list';
import { COLORS } from '../../../utils/theme/colors';
import { HeaderIcon } from '../../molecules/HeaderIcon/HeaderIcon';
import { ListSkeleton } from '../../molecules/ListSkeleton/ListSkeleton';
import { Search } from '../../molecules/Search';
import CardListConstructor from '../CardListConstructor/CardListConstructor';
import { closeAllCards } from '../CardListConstructor/helpers';
import EmptySearch from '../EmptySearch';

export const StationGroupTable: React.FC = () => {
  const [cpGroupsDataList, setCpGroupsDataList] = useState<ChargePointGroupProps[]>([]);
  const [loading, setLoading] = useState<any>(false);
  const [data, setdata] = useState<any>({});
  const [paginationData, setpaginationData] = useState<any>([]);
  const [searchInput, setsearchInput] = useState('');

  const childRef: any = useRef();

  const routes = useSelector((state: any) => state.user.routes);

  const paginate = () => childRef?.current?.paginate();

  useEffect(() => {
    (async function () {
      try {
        setLoading(true);
        const cpGroups = await getChargePointGroups();
        setCpGroupsDataList(cpGroups.list);
        setLoading(false);
      } catch {
        setLoading(false);
      }
    })();
  }, []);

  const HEADER = useMemo(() => [
    {
      text: T('Name'),
      sideIcon: <HeaderIcon />,
      action: (e: React.MouseEvent<HTMLDivElement>) =>
        orderByName(
          cpGroupsListFilter(searchInput, cpGroupsDataList),
          (newList) => {
            setIconActive(e);
            closeAllCards();
            paginate();
            setdata(combiningObjects(newList, HEADER));
            setpaginationData(newList);
          },
          e,
        ),
      size: '30%',
      dropdown: <></>,
    },
    {
      text: T('Address'),
      sideIcon: <HeaderIcon />,
      action: (e: React.MouseEvent<HTMLDivElement>) =>
        orderByAddress(
          cpGroupsListFilter(searchInput, cpGroupsDataList),
          (newList) => {
            setIconActive(e);
            closeAllCards();
            paginate();
            setdata(combiningObjects(newList, HEADER));
            setpaginationData(newList);
          },
          e,
        ),
      size: '50%',
      dropdown: <></>,
    },
    {
      text: 'External ID',
      sideIcon: <HeaderIcon />,
      action: (e: React.MouseEvent<HTMLDivElement>) =>
        orderByExternalId(
          cpGroupsListFilter(searchInput, cpGroupsDataList),
          (newList) => {
            setIconActive(e);
            closeAllCards();
            paginate();
            setdata(combiningObjects(newList, HEADER));
            setpaginationData(newList);
          },
          e,
        ),
      size: '20%',
      dropdown: <></>,
    },
  ], [cpGroupsDataList, searchInput]);

  useEffect(() => {
    setdata(combiningObjects(cpGroupsDataList, HEADER));
    setpaginationData(cpGroupsDataList);
  }, [cpGroupsDataList, HEADER]);

  type FilterCpGroups = (e: any) => void;
  const filterCpGroups: FilterCpGroups = useCallback(
    (value) => {
      setsearchInput(value);
    },
    [],
  );

  const clearSearch = useCallback(() => {
    setsearchInput('');
    setdata(combiningObjects(cpGroupsDataList, HEADER));
  }, [HEADER, cpGroupsDataList]);

  useEffect(() => {
    setpaginationData(cpGroupsListFilter(searchInput, cpGroupsDataList));
    setdata(combiningObjects(cpGroupsListFilter(searchInput, cpGroupsDataList), HEADER));
  }, [searchInput, HEADER, cpGroupsDataList]);

  useEffect(() => {
    paginate();
  }, [paginationData]);

  return (
    <Div>
      <div className='tools_bar'>
        <h1 className='page_title'>{T('Chargebox Groups')}</h1>
        <Search
          className='search_input'
          placeholder={T('Search by name')}
          action={filterCpGroups}
          closeBtn={clearSearch}
          value={searchInput}
        />
        {routes && routes.find(({ path }) => path === '/station_groups/create') ? (
          <Link className='register_button cursor' to='/station_groups/create'>
            {T('New chargebox group')}
          </Link>
        ) : null}
      </div>
      {loading ? (
        <ListWrapper style={{ paddingTop: 20 }}>
          <ListSkeleton />
        </ListWrapper>
      ) : !loading && !data?.cardsList?.length ? (
        <EmptySearch />
      ) : (
        <ListWrapper>
          <CardListConstructor
            childRef={childRef}
            data={data}
            InnerComponent={StationGroupCardInnerContent}
            paginationCallback={(newList) => setdata(combiningObjects(newList, HEADER))}
            pureData={cpGroupsListFilter(searchInput, paginationData)}
          />
        </ListWrapper>
      )}
    </Div>
  );
};

const ListWrapper = styled.div`
  padding-left: 24px;
  padding-right: 24px;
`;

const Div = styled.div`
  padding-bottom: 60px;
  width: 100%;

  .header_item {
    font-size: 13px;
  }

  .card_header .card_value_label .main_card_text {
    font-size: 12px;
  }
  .thunder_icon {
    width: 20px;
    margin-right: 5px;
  }

  .chager_plug_icon {
    width: 20px;
    margin-right: 5px;
  }

  .tools_bar {
    display: flex;
    flex-direction: row;
    width: 98%;
    margin-right: 2%;
    align-items: center;
    justify-content: flex-end;

    .page_title {
      flex: auto;
      margin: 20px 0 0 2%;
      font-size: 21px;
      font-weight: normal;
      font-family: 'Roboto';
      color: #777;
    }
  }

  .search_input {
    width: 40%;
    margin-top: 23px;
  }

  .search_input input {
    box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.1);
    padding: 0 70px 0 30px;
    width: 100%;
    border-radius: 8px;
  }
  .register_button {
    background-color: ${COLORS.transparentDark};
    margin: 20px 2% 0;
    padding: 10px 30px;
    border-radius: 8px;
    font-size: 13px;
    color: #444;
    text-decoration: none;
    transition: background-color 200ms ease-in-out;

    :hover {
      background-color: ${({ theme }) => theme.hoveredDiv};
      color: ${COLORS.primary};
    }
  }

  .card_in_list.gabrr_expanded {
    height: 150px;
  }

  .card_in_list.gabrr_expanded .card_inner_content {
    height: calc(100% - 65px);
  }
`;
